import {useCallback, useEffect, useState, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, InputRange, MediaDeviceWrapper} from 'components';
import {DeviceKind} from 'enums';
import {TRANSLATION_KEYS} from 'locale';

const TEST_SOUND_URL = '/assets/sounds/ring.mp3';

const T_KEYS = TRANSLATION_KEYS.COMMON.BUTTONS;

const MIN_VOLUME = 0;
const MAX_VOLUME = 10;

type Props = {
    value: string;
    devices: MediaDeviceInfo[];
    onChange: (deviceId: string) => void;
};

export const AudioOutputDevice: React.FC<Props> = ({
    devices, value, onChange
}) => {
    const {t} = useTranslation();
    const audioRef = useRef<HTMLAudioElement>(new Audio(TEST_SOUND_URL));
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(MAX_VOLUME / 2);

    const isSinkIdAvailable = useMemo(() => !audioRef.current || audioRef.current &&
        typeof (audioRef as any).current.sinkId !== 'undefined', []);

    const handleAudioOutputDeviceChange = useCallback((deviceId: string) => {
        audioRef.current.pause();
        setIsPlaying(false);
        onChange(deviceId);
        audioRef.current.loop = false;
        if (isSinkIdAvailable) {
            (audioRef as any).current?.setSinkId?.(deviceId);
        }
    }, [isSinkIdAvailable, onChange]);

    const handleClick = useCallback(() => {
        setIsPlaying((prev) => !prev);
    }, []);

    const handleVolumeChange = useCallback((newValue: number) => {
        setVolume(newValue);
    }, []);

    useEffect(() => {
        if (isPlaying) {
            if (isSinkIdAvailable) {
                (audioRef as any).current?.setSinkId?.(value);
            }
            audioRef.current.volume = volume / (MAX_VOLUME - MIN_VOLUME);
            audioRef.current.loop = true;
            audioRef.current?.play();
        } else {
            audioRef.current.loop = false;
            audioRef.current?.pause();
        }
    }, [value, isPlaying, volume, isSinkIdAvailable]);

    useEffect(() => () => {
        audioRef.current.pause();
    }, []);

    return (
        <MediaDeviceWrapper
            type={DeviceKind.Speaker}
            devices={devices}
            selectedDeviceId={value}
            onDeviceChange={handleAudioOutputDeviceChange}
        >
            <div className='tw-mt-12 tw-mb-8 tw-flex tw-justify-between tw-items-center'>
                <InputRange value={volume} min={MIN_VOLUME} max={MAX_VOLUME} hasLabels onChange={handleVolumeChange} />
                <Button className='tw-ml-5' width='small' onClick={handleClick}>
                    <span>{isPlaying ? t(T_KEYS.STOP) : t(T_KEYS.TEST)}</span>
                </Button>
            </div>
        </MediaDeviceWrapper>
    );
};